/**
 * Fixed headings, labels and images for the Contentful product sub-category pages
 * (`/<baseSlug>/<slug>`), rendered by `components/Contentful/SubProduct.tsx`. The section copy
 * comes from the Contentful entry, except the What Our Customers Say carousel
 * (`components/Shared/WhatOurCustomers/RefactorContent.tsx`). Parallel to
 * `./productStaticContent.ts` (category pages): `ApplicationsStaticContent` and
 * `ByTheNumbersStaticContent` are the same copy, and `CharacteristicsStaticContent` adds the
 * grade-table headings. Not a page module: no `MetaObject` or `PageContent`, and not in the site
 * search index. The dormant Southern Tool Steel build uses it too, despite the `ryerson` folder.
 */
import { StaticApplicationsContent } from "@components/Products/Applications";
import { StaticByTheNumbers } from "@components/Products/ByTheNumbers";
import { StaticCharacteristicsContent } from "@components/Products/Characteristics";
import { StaticHeroContent } from "@components/Products/SubcategoryHero/Hero";
import { LanguageContent } from "@ryerson/frontend.common";

/** Hero labels and Shop By Shape column headings; the main button goes to `/store`. */
export const SubCategoryHeroStaticContent: LanguageContent<StaticHeroContent> = {
	en: {
		buttonLabel: "Help me find the right metal",
		buttonUrl: "/store",
		generalReference: "General References",
		materialProperties: "Material Properties",
		selectionGuide: "Selection Guide",
		shopByShape: "Shop By Shape",
		shopByShapeHeader: {
			shape: "Shape",
			grade: "Grade",
			type: "Type",
			size: "Size",
		},
		shopNow: "Shop Now",
	},
	fr: {
		buttonLabel: "Aidez-moi à trouver le bon métal",
		buttonUrl: "/store",
		generalReference: "Référence Générale",
		materialProperties: "Propriétés Matérielles",
		selectionGuide: "Guide de sélection",
		shopByShape: "Magasiner par forme",
		shopByShapeHeader: {
			shape: "Forme",
			grade: "Grade",
			type: "Taper",
			size: "Dimensions",
		},
		shopNow: "Achetez maintenant",
	},
	es: {
		buttonLabel: "Ayúdame a encontrar el metal adecuado",
		buttonUrl: "/store",
		generalReference: "Referencia General",
		materialProperties: "Propiedades Materiales",
		selectionGuide: "Guía de selección",
		shopByShape: "Comprar por forma",
		shopByShapeHeader: {
			shape: "Forma",
			grade: "Grado",
			type: "Tipo",
			size: "Tamaño",
		},
		shopNow: "Compra ahora",
	},
};

/** Applications section heading, nav label and icon. */
export const ApplicationsStaticContent: LanguageContent<StaticApplicationsContent> = {
	en: {
		title: "Applications",
		navLabel: "Applications",
		icon: "gear",
	},
	fr: {
		title: "Applications",
		navLabel: "Applications",
		icon: "gear",
	},
	es: {
		title: "Aplicaciones",
		navLabel: "Aplicaciones",
		icon: "gear",
	},
};

/**
 * Characteristics & Properties title words, nav label, shared card images, and the grade-table
 * column headings that switch the table on.
 */
export const CharacteristicsStaticContent: LanguageContent<StaticCharacteristicsContent> = {
	en: {
		title1: "Characteristics",
		title2: "& Properties",
		navLabel: "Characteristics and Properties",
		desktopImage: "/images/products/CharacteristicsPropertiesDesktop.jpg",
		mobileImage: "/images/products/CharacteristicsPropertiesMobile.jpg",
		gradeTableHeadings: {
			grade: "Grade",
			alloyingElement: "Primary Alloying Elements",
			characteristics: "Characteristics",
		},
	},
	fr: {
		title1: "Caractéristiques",
		title2: " et propriétés",
		navLabel: "Caractéristiques et propriétés ",
		desktopImage: "/images/products/CharacteristicsPropertiesDesktop.jpg",
		mobileImage: "/images/products/CharacteristicsPropertiesMobile.jpg",
		gradeTableHeadings: {
			grade: "Grade",
			alloyingElement: "Éléments d'alliage primaire",
			characteristics: "Les caractéristiques",
		},
	},
	es: {
		title1: "Características",
		title2: " y Propiedades",
		navLabel: "Características y Propiedades",
		desktopImage: "/images/products/CharacteristicsPropertiesDesktop.jpg",
		mobileImage: "/images/products/CharacteristicsPropertiesMobile.jpg",
		gradeTableHeadings: {
			grade: "Grado",
			alloyingElement: "Elementos de aleación primaria",
			characteristics: "Características",
		},
	},
};

/** By The Numbers section heading and nav label. */
export const ByTheNumbersStaticContent: LanguageContent<StaticByTheNumbers> = {
	en: {
		title: "By The Numbers",
		navLabel: "By The Numbers",
	},
	fr: {
		title: "Par les nombres",
		navLabel: "Par les nombres",
	},
	es: {
		title: "Por los números",
		navLabel: "Por los números",
	},
};
